import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, Search } from "lucide-react";

import { AdminAPI } from "@/lib/admin-api";
import { Card, CardBody } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { formatDateTime, truncateMiddle } from "@/lib/format";
import { useDebounce } from "@/hooks/use-debounce";
import type { AuditEntry } from "@/types/admin";

export function AuditLogPage() {
  const [engine, setEngine] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [correlationId, setCorrelationId] = useState("");
  const [selected, setSelected] = useState<AuditEntry | null>(null);
  const debSubject = useDebounce(subjectId, 300);
  const debCorr = useDebounce(correlationId, 300);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-audit", engine, debSubject, debCorr],
    queryFn: () => AdminAPI.listAudit({
      ...(engine ? { engine } : {}),
      ...(debSubject ? { subject_id: debSubject } : {}),
      ...(debCorr ? { correlation_id: debCorr } : {}),
    }),
  });

  const rows = data ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Audit Log</h1>
        <p className="text-sm text-surface-500 mt-1">
          Immutable audit trail. Filter by engine, subject, or correlation ID for full causal reconstruction.
        </p>
      </header>

      <Card>
        <div className="p-5 border-b border-surface-200 grid grid-cols-3 gap-3">
          <Select
            options={[
              { value: "",              label: "All engines"      },
              { value: "authentication",label: "Authentication"   },
              { value: "order",         label: "Order"            },
              { value: "payment",       label: "Payment"          },
              { value: "qualification", label: "Qualification"    },
              { value: "membership",    label: "Membership"       },
              { value: "club_entry",    label: "Club Entry"       },
              { value: "placement",     label: "Placement"        },
              { value: "stage",         label: "Stage"            },
              { value: "reward",        label: "Reward"           },
              { value: "wallet",        label: "Wallet"           },
              { value: "ledger",        label: "Ledger"           },
              { value: "completion",    label: "Completion"       },
              { value: "reentry",       label: "Re-entry"         },
              { value: "notification",  label: "Notification"     },
            ]}
            value={engine}
            onChange={(e) => setEngine(e.target.value)}
          />
          <Input placeholder="Subject ID (e.g., ENT000001)"
                 leftIcon={<Search className="h-4 w-4" />}
                 value={subjectId} onChange={(e) => setSubjectId(e.target.value)} />
          <Input placeholder="Correlation ID"
                 leftIcon={<Search className="h-4 w-4" />}
                 value={correlationId} onChange={(e) => setCorrelationId(e.target.value)} />
        </div>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState icon={FileText} title="No audit entries found" />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>When</TH><TH>Engine</TH><TH>Action</TH>
                  <TH>Actor</TH><TH>Subject</TH><TH>Correlation</TH>
                </TR>
              </THead>
              <tbody>
                {rows.map((a) => (
                  <TR key={a.id} onClick={() => setSelected(a)}>
                    <TD className="text-2xs text-surface-500">{formatDateTime(a.created_at)}</TD>
                    <TD><Badge tone="info" className="capitalize">{a.engine.replace(/_/g, " ")}</Badge></TD>
                    <TD className="font-mono text-xs">{a.action}</TD>
                    <TD className="text-xs">
                      <span className="capitalize">{a.actor_type}</span>
                      {a.actor_id && <span className="text-surface-500"> · {a.actor_id}</span>}
                    </TD>
                    <TD className="font-mono text-xs">
                      {a.subject_type ? `${a.subject_type} · ${a.subject_id}` : "—"}
                    </TD>
                    <TD className="font-mono text-2xs">{truncateMiddle(a.correlation_id, 6)}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {selected && (
        <Modal open onClose={() => setSelected(null)} title="Audit Entry" size="lg">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-2xs uppercase text-surface-500 font-semibold">Engine</span>
                <div className="mt-0.5 capitalize">{selected.engine}</div></div>
              <div><span className="text-2xs uppercase text-surface-500 font-semibold">Action</span>
                <div className="mt-0.5 font-mono">{selected.action}</div></div>
              <div><span className="text-2xs uppercase text-surface-500 font-semibold">Actor</span>
                <div className="mt-0.5 capitalize">{selected.actor_type} · {selected.actor_id || "—"}</div></div>
              <div><span className="text-2xs uppercase text-surface-500 font-semibold">Subject</span>
                <div className="mt-0.5 font-mono">{selected.subject_type}: {selected.subject_id}</div></div>
              <div className="col-span-2">
                <span className="text-2xs uppercase text-surface-500 font-semibold">Correlation ID</span>
                <div className="mt-0.5 font-mono text-xs">{selected.correlation_id}</div>
              </div>
            </div>
            {selected.previous_state && (
              <div>
                <div className="text-2xs uppercase text-surface-500 font-semibold mb-1">Previous state</div>
                <pre className="text-xs bg-danger-50/60 border border-danger-100 rounded-lg p-3 overflow-auto max-h-48 font-mono">
                  {JSON.stringify(selected.previous_state, null, 2)}
                </pre>
              </div>
            )}
            {selected.new_state && (
              <div>
                <div className="text-2xs uppercase text-surface-500 font-semibold mb-1">New state</div>
                <pre className="text-xs bg-success-50/60 border border-success-100 rounded-lg p-3 overflow-auto max-h-48 font-mono">
                  {JSON.stringify(selected.new_state, null, 2)}
                </pre>
              </div>
            )}
            {selected.metadata && Object.keys(selected.metadata).length > 0 && (
              <div>
                <div className="text-2xs uppercase text-surface-500 font-semibold mb-1">Metadata</div>
                <pre className="text-xs bg-surface-50 border border-surface-200 rounded-lg p-3 overflow-auto max-h-48 font-mono">
                  {JSON.stringify(selected.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
